/**
 * STAT CARD COMPONENT - Apple Liquid Glass Design System
 * macOS Tahoe 26.2 & iOS 26.2
 * Based on WWDC25: Meet Liquid Glass
 *
 * Features:
 * - Liquid Glass with lensing and refraction
 * - Gradient icon backgrounds with animated shimmer
 * - Adaptive tinting based on metric type
 * - Morphing hover animations with "lift" effect
 * - Pearlescent shimmer on interaction
 * - Specular highlights
 */

import { ReactNode, createElement, isValidElement } from 'react'
import { motion } from 'framer-motion'
import { ArrowUp, ArrowDown, Minus, TrendingUp, TrendingDown, LucideIcon } from 'lucide-react'
import { cn } from '../../lib/utils'

interface StatCardProps {
  title: string
  value: string | number
  icon: ReactNode | LucideIcon
  trend?: {
    value: number
    label: string
    type?: 'percentage' | 'absolute'
  }
  color?: 'blue' | 'green' | 'purple' | 'orange' | 'red' | 'indigo' | 'teal' | 'pink' | 'yellow' | 'cyan'
  onClick?: () => void
  subtitle?: string
  size?: 'sm' | 'md' | 'lg'
  badge?: ReactNode
}

const colorClasses = {
  blue: {
    gradient: 'from-blue via-blue-dark to-indigo',
    glass: 'bg-blue-glass dark:bg-blue-glass',
    text: 'text-blue dark:text-blue-dark',
    ring: 'ring-blue/20 dark:ring-blue-dark/20'
  },
  green: {
    gradient: 'from-green via-green-dark to-teal',
    glass: 'bg-green-glass dark:bg-green-glass',
    text: 'text-green dark:text-green-dark',
    ring: 'ring-green/20 dark:ring-green-dark/20'
  },
  purple: {
    gradient: 'from-purple via-purple-dark to-pink',
    glass: 'bg-purple-glass dark:bg-purple-glass',
    text: 'text-purple dark:text-purple-dark',
    ring: 'ring-purple/20 dark:ring-purple-dark/20'
  },
  orange: {
    gradient: 'from-orange via-orange-dark to-red',
    glass: 'bg-orange-glass dark:bg-orange-glass',
    text: 'text-orange dark:text-orange-dark',
    ring: 'ring-orange/20 dark:ring-orange-dark/20'
  },
  red: {
    gradient: 'from-red via-red-dark to-pink',
    glass: 'bg-red-glass dark:bg-red-glass',
    text: 'text-red dark:text-red-dark',
    ring: 'ring-red/20 dark:ring-red-dark/20'
  },
  indigo: {
    gradient: 'from-indigo via-indigo-dark to-purple',
    glass: 'bg-indigo-glass dark:bg-indigo-glass',
    text: 'text-indigo dark:text-indigo-dark',
    ring: 'ring-indigo/20 dark:ring-indigo-dark/20'
  },
  teal: {
    gradient: 'from-teal via-teal-dark to-blue',
    glass: 'bg-teal-glass dark:bg-teal-glass',
    text: 'text-teal dark:text-teal-dark',
    ring: 'ring-teal/20 dark:ring-teal-dark/20'
  },
  pink: {
    gradient: 'from-pink via-pink-dark to-purple',
    glass: 'bg-pink-glass dark:bg-pink-glass',
    text: 'text-pink dark:text-pink-dark',
    ring: 'ring-pink/20 dark:ring-pink-dark/20'
  },
  yellow: {
    gradient: 'from-yellow via-orange to-orange-dark',
    glass: 'bg-yellow/10 dark:bg-yellow/15',
    text: 'text-yellow dark:text-yellow',
    ring: 'ring-yellow/20 dark:ring-yellow/20'
  },
  cyan: {
    gradient: 'from-cyan via-teal to-blue',
    glass: 'bg-cyan/10 dark:bg-cyan/15',
    text: 'text-cyan dark:text-cyan',
    ring: 'ring-cyan/20 dark:ring-cyan/20'
  }
}

export function StatCard({
  title,
  value,
  icon,
  trend,
  color = 'blue',
  onClick,
  subtitle,
  size = 'md',
  badge
}: StatCardProps) {
  const colors = colorClasses[color]

  const sizeClasses = {
    sm: {
      padding: 'p-4',
      iconWrapper: 'p-2.5',
      iconSize: 'w-5 h-5',
      value: 'text-title2',
      title: 'text-caption1',
      trend: 'text-caption2'
    },
    md: {
      padding: 'p-6',
      iconWrapper: 'p-3.5',
      iconSize: 'w-6 h-6',
      value: 'text-display',
      title: 'text-subheadline',
      trend: 'text-footnote'
    },
    lg: {
      padding: 'p-8',
      iconWrapper: 'p-4',
      iconSize: 'w-7 h-7',
      value: 'text-largeTitle',
      title: 'text-body',
      trend: 'text-subheadline'
    }
  }

  const sizes = sizeClasses[size]

  // Spring animation config
  const springConfig = {
    type: "spring",
    damping: 20,
    stiffness: 300,
    mass: 0.8
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20, scale: 0.95 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      whileHover={{
        y: -8,
        scale: 1.02,
        transition: springConfig
      }}
      whileTap={onClick ? { scale: 0.98 } : {}}
      onClick={onClick}
      transition={springConfig}
      className={cn(
        'relative overflow-hidden',
        'liquid-material-regular liquid-lens rounded-2xl',
        'shadow-glass dark:shadow-dark-glass',
        'hover:shadow-glass-lg dark:hover:shadow-dark-glass-lg',
        'transition-shadow duration-base ease-liquid-out',
        onClick && 'cursor-pointer'
      )}
    >
      {/* Animated gradient background */}
      <div className="absolute -top-24 -right-24 w-48 h-48 opacity-[0.06] dark:opacity-[0.10]">
        <motion.div
          className={cn(
            'w-full h-full bg-gradient-to-br rounded-full blur-3xl',
            colors.gradient
          )}
          animate={{
            scale: [1, 1.1, 1],
            rotate: [0, 10, 0]
          }}
          transition={{
            duration: 8,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        />
      </div>

      {/* Pearlescent shimmer layer */}
      <div className="absolute inset-0 opacity-0 hover:opacity-100 transition-opacity duration-slow pointer-events-none">
        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -translate-x-full hover:translate-x-full transition-transform duration-slow" />
      </div>

      {/* Specular highlight */}
      <div className="absolute inset-0 bg-gradient-to-br from-white/15 to-transparent dark:from-white/8 pointer-events-none" />

      <div className={cn('relative', sizes.padding)}>
        {/* Header with icon and trend */}
        <div className="flex items-start justify-between mb-4">
          {/* Icon with glass container */}
          <motion.div
            className="relative"
            whileHover={{ scale: 1.08, rotate: 5 }}
            transition={{ type: "spring", damping: 15, stiffness: 300 }}
          >
            <div className={cn(
              'rounded-2xl overflow-hidden relative group',
              sizes.iconWrapper,
              colors.glass,
              'shadow-glass-sm dark:shadow-dark-glass-sm',
              `ring-1 ${colors.ring}`
            )}>
              {/* Inner glow on hover */}
              <div className="absolute inset-0 bg-gradient-to-br from-white/30 to-transparent dark:from-white/15 opacity-0 group-hover:opacity-100 transition-opacity duration-fast" />

              <div className={cn(
                'relative transition-transform duration-fast group-hover:scale-110',
                sizes.iconSize,
                colors.text
              )}>
                {isValidElement(icon) ? icon : createElement(icon as any)}
              </div>
            </div>
          </motion.div>

          {/* Trend indicator */}
          {trend && (
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              className={cn(
                'flex items-center gap-1.5 px-2.5 py-1.5 rounded-xl',
                'liquid-material-clear',
                'shadow-glass-xs dark:shadow-dark-glass-xs'
              )}
            >
              {trend.value > 0 ? (
                <TrendingUp className="w-4 h-4 text-green dark:text-green-dark" strokeWidth={2.5} />
              ) : trend.value < 0 ? (
                <TrendingDown className="w-4 h-4 text-red dark:text-red-dark" strokeWidth={2.5} />
              ) : (
                <Minus className="w-4 h-4 text-light-text-tertiary dark:text-dark-text-tertiary" strokeWidth={2.5} />
              )}
              <span className={cn(
                sizes.trend,
                'font-bold tracking-tight',
                trend.value > 0
                  ? 'text-green dark:text-green-dark'
                  : trend.value < 0
                  ? 'text-red dark:text-red-dark'
                  : 'text-light-text-tertiary dark:text-dark-text-tertiary'
              )}>
                {trend.value > 0 && '+'}{Math.abs(trend.value)}{trend.type === 'percentage' ? '%' : ''}
              </span>
            </motion.div>
          )}
        </div>

        {/* Value with tabular numbers */}
        <div className="mb-2">
          <motion.h3
            className={cn(
              sizes.value,
              'font-bold text-light-text-primary dark:text-dark-text-primary tracking-tight tabular-nums'
            )}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            {value}
          </motion.h3>
        </div>

        {/* Title and trend label */}
        <div className="flex items-center justify-between gap-3">
          <div>
            <div className="flex items-center gap-2">
              <p className={cn(
                sizes.title,
                'font-semibold text-light-text-secondary dark:text-dark-text-secondary tracking-tight'
              )}>
                {title}
              </p>
              {badge && <div>{badge}</div>}
            </div>
            {subtitle && (
              <p className="text-caption2 text-light-text-tertiary dark:text-dark-text-tertiary mt-0.5">
                {subtitle}
              </p>
            )}
          </div>
          {trend && (
            <p className="text-caption2 text-light-text-tertiary dark:text-dark-text-tertiary font-medium tracking-tight">
              {trend.label}
            </p>
          )}
        </div>
      </div>

      {/* Bottom edge highlight */}
      <div className="absolute bottom-0 left-4 right-4 h-px bg-gradient-to-r from-transparent via-white/20 to-transparent dark:via-white/10" />
    </motion.div>
  )
}

/**
 * MiniStatCard - Compact stat for tight spaces
 */
interface MiniStatCardProps {
  label: string
  value: string | number
  trend?: number
  color?: keyof typeof colorClasses
}

export function MiniStatCard({ label, value, trend, color = 'blue' }: MiniStatCardProps) {
  const colors = colorClasses[color]

  return (
    <div className={cn(
      'flex items-center gap-3 px-4 py-3 rounded-xl',
      'liquid-material-clear',
      'shadow-glass-xs dark:shadow-dark-glass-xs'
    )}>
      {/* Color indicator */}
      <div className={cn('w-1 h-8 rounded-full bg-gradient-to-b', colors.gradient)} />

      <div className="flex-1 min-w-0">
        <p className="text-caption1 text-light-text-tertiary dark:text-dark-text-tertiary truncate">
          {label}
        </p>
        <p className="text-headline font-bold text-light-text-primary dark:text-dark-text-primary tabular-nums">
          {value}
        </p>
      </div>

      {trend !== undefined && (
        <div className={cn(
          'flex items-center gap-1',
          trend > 0 ? 'text-green dark:text-green-dark' : trend < 0 ? 'text-red dark:text-red-dark' : 'text-light-text-tertiary'
        )}>
          {trend > 0 ? (
            <ArrowUp className="w-3.5 h-3.5" strokeWidth={2.5} />
          ) : trend < 0 ? (
            <ArrowDown className="w-3.5 h-3.5" strokeWidth={2.5} />
          ) : null}
          <span className="text-caption1 font-bold">{Math.abs(trend)}%</span>
        </div>
      )}
    </div>
  )
}

/**
 * StatCardGrid - Grid layout for stat cards
 */
interface StatCardGridProps {
  children: ReactNode
  columns?: 2 | 3 | 4
}

export function StatCardGrid({ children, columns = 4 }: StatCardGridProps) {
  const columnClasses = {
    2: 'grid-cols-1 sm:grid-cols-2',
    3: 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3',
    4: 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-4'
  }

  return (
    <div className={cn('grid gap-4 lg:gap-6', columnClasses[columns])}>
      {children}
    </div>
  )
}
